import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Bot, User, ChevronLeft, ChevronRight } from "lucide-react";
import { S, MANROPE, FOCUS, API, auDate } from "./lib";
import { actorLine } from "./SocialHistory";

const shift = (day, by) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + by);
  return d.toISOString().slice(0, 10);
};
const time = (iso) => new Date(iso).toLocaleTimeString("en-AU",
  { hour: "2-digit", minute: "2-digit" });

/* The morning scan: everything that changed yesterday or today, so Claude's
   drafting can be read rather than policed. */
export default function SocialActivity({ onClose, onOpenPost }) {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [onlyClaude, setOnlyClaude] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try {
      const r = await axios.get(`${API}/activity`,
        { params: { day, ...(onlyClaude ? { actor: "claude" } : {}) } });
      setData(r.data);
    } catch { setData({ entries: [], total: 0 }); }
  }, [day, onlyClaude]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-[95] flex justify-end" data-testid="social-activity">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.32)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[560px] overflow-y-auto"
        style={{ background: S.panel, borderLeft: `1px solid ${S.border}` }}>
        <div className="sticky top-0 z-10 px-5 py-3.5 flex items-center gap-2 flex-wrap"
          style={{ background: S.card, borderBottom: `1px solid ${S.border}` }}>
          <div>
            <div style={{ ...MANROPE, fontSize: 16, fontWeight: 800, color: S.ink }}>
              What changed
            </div>
            <div className="text-[11.5px]" style={{ color: S.ink2 }}>
              {data ? `${data.by_claude ?? 0} through Claude · ${data.by_people ?? 0} by hand` : "Loading…"}
            </div>
          </div>
          <button onClick={onClose} data-testid="social-activity-close"
            className={`ml-auto w-9 h-9 rounded-[7px] flex items-center justify-center ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, background: S.card }}><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${S.divider}` }}>
          <button onClick={() => setDay(shift(day, -1))} data-testid="social-activity-prev"
            className={`w-8 h-8 rounded-[7px] flex items-center justify-center ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, background: S.card }}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[12.5px]" style={{ color: S.ink }} data-testid="social-activity-day">{auDate(day)}</span>
          <button onClick={() => setDay(shift(day, 1))} data-testid="social-activity-next"
            className={`w-8 h-8 rounded-[7px] flex items-center justify-center ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, background: S.card }}>
            <ChevronRight className="w-4 h-4" />
          </button>
          <label className="ml-auto flex items-center gap-1.5 text-[12px]" style={{ color: S.ink2 }}>
            <input type="checkbox" checked={onlyClaude} data-testid="social-activity-only-claude"
              onChange={(e) => setOnlyClaude(e.target.checked)} />
            Through Claude only
          </label>
        </div>

        <div className="px-5 py-4 space-y-2">
          {!data ? (
            <p className="text-[13px]" style={{ color: S.ink2 }}>Loading…</p>
          ) : data.entries.length === 0 ? (
            <p className="text-[13px]" style={{ color: S.ink2 }} data-testid="social-activity-empty">
              Nothing changed on the calendar that day.
            </p>
          ) : data.entries.map((e) => (
            <button key={e.id} onClick={() => onOpenPost?.(e.post_uid)}
              data-testid={`social-activity-entry-${e.id}`}
              className={`w-full text-left rounded-[9px] p-3 flex items-start gap-2 ${FOCUS}`}
              style={{ background: S.card, border: `1px solid ${S.border}` }}>
              {(e.actor_kind || "").startsWith("claude")
                ? <Bot className="w-4 h-4 shrink-0 mt-0.5" style={{ color: S.accentInk }} />
                : <User className="w-4 h-4 shrink-0 mt-0.5" style={{ color: S.ink3 }} />}
              <span className="min-w-0">
                <span className="text-[12.5px]" style={{ color: S.ink }}>
                  <b>{actorLine(e)}</b> · {e.tool.replace(/_/g, " ")} · <span className="mono">{e.post_id}</span>
                </span>
                <span className="block text-[12px]" style={{ color: S.ink2 }}>
                  {e.hook || "No hook yet"}
                </span>
                {e.reason && (
                  <span className="block text-[11.5px]" style={{ color: S.ink3 }}>“{e.reason}”</span>
                )}
                <span className="block text-[11px] mono" style={{ color: S.muted }}>
                  {time(e.at)} · {(e.changed || []).join(", ") || "no field change"}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
